import * as THREE from 'three';

let captureInProgress = false;

export function withCaptureSession(viewport, options, fn) {
  if (captureInProgress) throw new Error('CAPTURE_IN_PROGRESS');

  // Everything that only READS state stays outside the lock. Claiming the lock
  // first would mean a throw here (a lost WebGL context making getPixelRatio()
  // or pauseRendering() fail, say) skips the finally below and wedges
  // `captureInProgress` at true — every later capture would then fail with
  // CAPTURE_IN_PROGRESS until the page is reloaded. Nothing above the try
  // mutates anything, so failing here needs no cleanup.
  const {
    renderer,
    scene,
    helpers,
    pauseRendering = () => {},
    resumeRendering = () => {},
    render = () => {},
  } = viewport;
  const previousPixelRatio = renderer.getPixelRatio();
  const previousSize = renderer.getSize(new THREE.Vector2());
  const previousBackground = scene.background;
  const previousHelpersVisible = helpers?.visible;
  const visibility = options.objects.map((object) => [object, object.visible]);

  captureInProgress = true;
  try {
    // Inside the try: resumeRendering() in the finally is idempotent, so a
    // throwing pauseRendering() still unwinds cleanly.
    pauseRendering();
    renderer.setPixelRatio(1);
    renderer.setSize(options.width, options.height, false);
    scene.background = new THREE.Color(options.background);
    if (helpers && options.helpers === false) helpers.visible = false;
    if (options.keepIds) {
      for (const object of options.objects) {
        if (!options.keepIds.has(object.userData.anvilId)) object.visible = false;
      }
    }
    return fn();
  } finally {
    for (let index = visibility.length - 1; index >= 0; index--) {
      const [object, visible] = visibility[index];
      object.visible = visible;
    }
    if (helpers) helpers.visible = previousHelpersVisible;
    scene.background = previousBackground;
    renderer.setSize(previousSize.x, previousSize.y, false);
    renderer.setPixelRatio(previousPixelRatio);
    try {
      resumeRendering();
    } finally {
      captureInProgress = false;
      render();
    }
  }
}
